import React, { useEffect, useState, useCallback } from 'react';
import { getUsers, updateUser } from '../api';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const fetchUsers = useCallback(async (offset = 0) => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: '50', offset: String(offset) };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role = roleFilter;

      const data = await getUsers(params);
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, roleFilter]);

  useEffect(() => {
    fetchUsers(0);
  }, [fetchUsers]);

  const handleToggleActive = async (user) => {
    try {
      await updateUser(user.id, { isActive: !user.isActive });
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isActive: !u.isActive } : u)),
      );
    } catch {
      // handled
    }
  };

  const handleChangeRole = async (user, role) => {
    try {
      await updateUser(user.id, { role });
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, role } : u)),
      );
    } catch {
      // handled
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Users</h1>
        <p>Manage platform users</p>
      </div>

      <form className="toolbar" onSubmit={(e) => { e.preventDefault(); fetchUsers(0); }}>
        <input
          className="search-input"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select className="filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </form>

      {error && (
        <div className="empty-state" style={{ color: '#c62828', background: '#ffebee', padding: 16, borderRadius: 8, marginBottom: 16 }}>
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : !error && users.length === 0 ? (
        <div className="empty-state"><p>No users found.</p></div>
      ) : (
        <>
          <div className="card">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Location</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td style={{ fontWeight: 500 }}>{user.firstName} {user.lastName}</td>
                      <td style={{ fontSize: 13 }}>{user.email}</td>
                      <td style={{ fontSize: 13, color: '#888' }}>
                        {[user.city, user.province, user.country].filter(Boolean).join(', ')}
                      </td>
                      <td>
                        <select
                          className="filter-select"
                          style={{ padding: '4px 8px', fontSize: 12 }}
                          value={user.role}
                          onChange={(e) => handleChangeRole(user, e.target.value)}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <span className={`badge ${user.isActive ? 'badge-green' : 'badge-red'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>
                        {new Date(user.createdAt).toLocaleDateString('en-ZA')}
                      </td>
                      <td>
                        <button
                          className={`btn btn-sm ${user.isActive ? 'btn-danger' : 'btn-secondary'}`}
                          onClick={() => handleToggleActive(user)}
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pagination">
            <span>
              Showing {pagination.offset + 1}–{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.offset === 0}
                onClick={() => fetchUsers(Math.max(0, pagination.offset - pagination.limit))}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={!pagination.hasMore}
                onClick={() => fetchUsers(pagination.offset + pagination.limit)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
